import { projectsApi } from "@/services/rag-service";
import { createProjectManagementRequestScope } from "@/utils/requestScope";
import { normalizeProjectDto, normalizeProjectDtos } from "./adapters";
import { normalizeProjectListParams } from "./query-keys";
import {
  PERSONAL_PROJECT_SCOPE,
  type Project,
  type CreateProjectInput,
  type DeleteProjectInput,
  type ProjectDto,
  type ProjectListParams,
  type ProjectOrganizationScope,
  type UpdateProjectInput,
} from "./types";

const getProjectRequestScope = (organizationId: ProjectOrganizationScope) =>
  createProjectManagementRequestScope(
    organizationId === PERSONAL_PROJECT_SCOPE ? null : organizationId,
  );

export const listProjects = async ({
  organizationId,
  params = {},
}: {
  organizationId: ProjectOrganizationScope;
  params?: ProjectListParams;
}): Promise<Project[]> => {
  const normalizedParams = normalizeProjectListParams(params);
  const response = (await projectsApi.getProjects({
    ...normalizedParams,
    requestScope: getProjectRequestScope(organizationId),
  })) as ProjectDto[];

  return normalizeProjectDtos(response);
};

export const createProject = async ({
  organizationId,
  project,
}: {
  organizationId: ProjectOrganizationScope;
  project: CreateProjectInput;
}): Promise<Project> => {
  const response = (await projectsApi.createProject(
    {
      name: project.name,
      description: project.description || "",
      list_share_users: project.teamMembers || [],
    },
    getProjectRequestScope(organizationId),
  )) as ProjectDto;

  return normalizeProjectDto(response);
};

export const updateProject = async ({
  organizationId,
  project,
}: {
  organizationId: ProjectOrganizationScope;
  project: UpdateProjectInput;
}): Promise<Project> => {
  const response = (await projectsApi.updateProject(
    project.projectId,
    {
      name: project.name,
      description: project.description || "",
    },
    getProjectRequestScope(organizationId),
  )) as ProjectDto;

  return normalizeProjectDto(response);
};

export const deleteProject = async ({
  organizationId,
  project,
}: {
  organizationId: ProjectOrganizationScope;
  project: DeleteProjectInput;
}): Promise<boolean> =>
  projectsApi.deleteProject(
    project.projectId,
    project.permanent ?? false,
    project.force ?? false,
    getProjectRequestScope(organizationId),
  );
